/**
 * CLN_Break — the chart adapter.
 *
 * Turns bars into the marks you validate by eye. The RULE lives in
 * `clnBreakEngine.ts`, which imports nothing and has a byte-identical backend
 * twin (`scripts/check-engine-parity.mjs`, 5th pair), so the marks drawn here and
 * the numbers in `docs/CLNBREAK-RESULTS.md` come from the same bytes.
 *
 * WHAT IT DRAWS
 *   ▲ B   long  — price touched the top of the last three LR bodies
 *   ▼ S   short — price touched the bottom of them
 *   ■ X   stopped out (only when a stop is configured; the rule ships without one)
 *
 * The LEVELS themselves are drawn by the `clnBreakLevels` overlay, which calls
 * `computeClnBreakSeries` below — the same call this adapter makes. One
 * computation, so a line and a mark can never disagree about where the level was.
 *
 * NOT AN ENGINE. No scan, no alert, no order path. It marks a chart.
 *
 * THE MISTAKE THIS FILE EXISTS TO AVOID: the LR bodies come from
 * `computeClnBars`, never from `transformCandles(candles, 'CLN')`. That call is a
 * PASSTHROUGH — it returns the raw candle body, and a rule built on it looks
 * entirely plausible while measuring something else.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import {
  ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, ChartMarker,
} from './types';
import { computeClnBars, CLN_DEFAULT_LENGTH } from '../clnCandles';
import { atrSeries } from '../dailyBlastIndicators';
import {
  computeClnBreakSteps, computeClnBreakTrades, ClnBreakOptions, ClnBreakStep, ClnBreakTrade,
  CLN_BREAK_ATR_PERIOD, CLN_BREAK_DEFAULT_CLN_LENGTH, CLN_BREAK_DEFAULT_LOOKBACK,
} from './clnBreakEngine';

/** Marker colours. A chart-strategy adapter is PURE and cannot see the theme, so
 *  these have to read on white and on `#0D1117` alike — the reason Vajra's exit
 *  marks had to be lifted twice. Mid-tone saturated hues, checked in screenshots
 *  on both themes rather than assumed. */
export const CLN_BREAK_LONG_COLOR = '#00c853';
export const CLN_BREAK_SHORT_COLOR = '#ff5252';
export const CLN_BREAK_STOP_COLOR = '#ff9800';

/** Level line defaults, used when the settings group has not been resolved yet.
 *  The dialog owns the live values (`settings.clnBreak.*`). */
export const CLN_BREAK_BUY_LINE = '#43a047';
export const CLN_BREAK_SHORT_LINE = '#e53935';

export interface ClnBreakSeries {
  steps: ClnBreakStep[];
  trades: ClnBreakTrade[];
  /** The regression length actually used, after clamping. */
  clnLength: number;
}

/**
 * Resolve the rule's options out of the chart's option bag.
 *
 * THE REGRESSION LENGTH IS THE CHART'S (`settings.candle.clnLength`, handed in as
 * `chartClnLength`). The rule is "the previous three LR bodies" and the owner
 * means the three on the chart. Until 2026-09-24 the strategy PINNED its own
 * length (14, as measured) and only warned when the chart drew another — on the
 * owner's QQQXUSD 8H chart, drawn at 50, 30 of 63 marks sat on bars whose wick
 * never reached the bodies on screen, though every one breached the length-14
 * bodies it was actually built from. A separate dial made that divergence
 * possible, so there is no separate dial: one length, so nothing can disagree.
 */
export function resolveClnBreakOptions(opts?: ChartStrategyOptions): ClnBreakOptions {
  const num = (v: unknown, dflt: number): number => (Number.isFinite(Number(v)) ? Number(v) : dflt);
  return {
    clnLength: num(opts?.chartClnLength, CLN_BREAK_DEFAULT_CLN_LENGTH),
    lookback: num(opts?.clnBreakLookback, CLN_BREAK_DEFAULT_LOOKBACK),
    bufferAtr: num(opts?.clnBreakBufferAtr, 0),
    breach: opts?.clnBreakBreach === 'close' ? 'close' : 'touch',
    requireLrAgree: opts?.clnBreakRequireLrAgree === true,
    stopAtr: num(opts?.clnBreakStopAtr, 0),
  };
}

/**
 * The one computation. Both the marks and the level lines come from this, and the
 * ATR is only computed when a dial actually needs it.
 *
 * `candles` must be RAW market bars. Indicators never read the display candle
 * type — switching the chart to Heikin-Ashi must not move where a rule fires.
 */
export function computeClnBreakSeries(
  candles: CandleRow[],
  cfg: ClnBreakOptions,
): ClnBreakSeries {
  const clnLength = Number(cfg.clnLength) || CLN_BREAK_DEFAULT_CLN_LENGTH;
  const cln = computeClnBars(candles, clnLength);
  const needAtr = (Number(cfg.bufferAtr) || 0) > 0 || (Number(cfg.stopAtr) || 0) > 0;
  const atr = needAtr ? atrSeries(candles, CLN_BREAK_ATR_PERIOD) : undefined;
  const steps = computeClnBreakSteps(candles as any, cln as any, { ...cfg, atr });
  const trades = computeClnBreakTrades(candles as any, steps);
  return { steps, trades, clnLength };
}

export function computeClnBreakOverlay(
  candles: CandleRow[],
  opts?: ChartStrategyOptions,
): StrategyOverlayResult {
  const cfg = resolveClnBreakOptions(opts);
  const warmup = Math.max(Number(cfg.clnLength) || CLN_BREAK_DEFAULT_CLN_LENGTH, CLN_BREAK_ATR_PERIOD)
    + (Number(cfg.lookback) || CLN_BREAK_DEFAULT_LOOKBACK);
  if (!candles || candles.length < warmup + 2) {
    return {
      candleColors: [],
      markers: [],
      // Two SHORT lines, for the same reason the notices below are split.
      notice: 'Needs ' + (warmup + 2) + ' bars before a level exists; this chart has '
        + (candles ? candles.length : 0) + '.'
        + String.fromCharCode(10)
        + 'That is the regression window, the ATR window and the '
        + cfg.lookback + '-bar level window.',
    };
  }

  const { steps, trades, clnLength } = computeClnBreakSeries(candles, cfg);

  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();

  for (const s of steps) {
    positionByTime.set(s.time, s.pos);
    if (s.stopped) {
      markers.push({
        time: s.time, position: 'aboveBar', text: 'X',
        color: CLN_BREAK_STOP_COLOR, shape: 'circle', size: 2,
      });
      signalsByTime.set(s.time, { signal: 'EXIT_LONG', strength: null, pos: 'FLAT', reason: 'CLNB_STOP' });
      continue;
    }
    if (!s.signal) continue;
    const long = s.signal === 'BUY';
    markers.push({
      time: s.time,
      position: long ? 'belowBar' : 'aboveBar',
      text: long ? 'B' : 'S',
      color: long ? CLN_BREAK_LONG_COLOR : CLN_BREAK_SHORT_COLOR,
      shape: long ? 'arrowUp' : 'arrowDown',
      size: 2,
    });
    signalsByTime.set(s.time, {
      signal: long ? 'BUY' : 'SHORT',
      strength: s.ambiguous ? 'LOW' : 'MEDIUM',
      pos: long ? 'LONG' : 'SHORT',
      reason: s.reason || null,
    });
  }

  /* The honest notices — facts about THIS chart, computed here, not generic advice.
   *
   * ONE SHORT LINE EACH, JOINED BY NEWLINES. The badge draws every entry as a
   * single UNWRAPPED row (see strategyNoticeBadge in ChartsPage), so a paragraph
   * runs straight off the pane — which is exactly what the first screenshot
   * showed: three sentences concatenated with spaces, clipped mid-word at the
   * right edge on both themes. */
  const notices: string[] = [];
  // The levels follow the chart's CLN length. The study measured only the
  // default, so say so when the chart is on any other.
  if (Math.round(clnLength) !== CLN_BREAK_DEFAULT_CLN_LENGTH) {
    notices.push('Levels use the chart\'s CLN length ' + Math.round(clnLength)
      + '; the study measured ' + CLN_BREAK_DEFAULT_CLN_LENGTH + ' only.');
  }
  const levelBars = steps.filter(s => s.buyLevel != null).length;
  const ambig = steps.filter(s => s.ambiguous).length;
  if (levelBars > 0) {
    const pct = (100 * ambig) / levelBars;
    if (pct >= 5) {
      notices.push('Both levels broke on ' + pct.toFixed(0)
        + '% of bars — each resolved against the open position.');
    }
  }
  if (!trades.length) notices.push('No completed position on this range.');

  return {
    candleColors: [],
    markers,
    signalsByTime,
    positionByTime,
    ...(notices.length ? { notice: notices.join(String.fromCharCode(10)) } : {}),
  };
}

export const clnBreakStrategy: ChartStrategy = {
  id: 'clnbreak',
  name: 'CLN_Break',
  computeOverlays: computeClnBreakOverlay,
};

/** Re-exported so the overlay block in TradingChart does not have to reach into
 *  the engine for the one constant it needs. */
export { CLN_DEFAULT_LENGTH };
